import { useMemo, useState } from 'react';
import { useServicos } from '@/features/servicos/hooks/useServicos';
import { ServicoFormModal } from '@/features/servicos/pages/ServicoFormModal';
import type { ServicoResumo } from '@/features/servicos/api/servicosApi';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Paginacao } from '@/shared/ui/Paginacao';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import { IconEdit, IconRefresh, IconTrash, IconWrench } from '@/shared/ui/Icon';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { useRotaEntidade } from '@/app/useRotaEntidade';
import { ROTAS } from '@/app/rotas';

const formatadorMoeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

type ModoForm =
  | { tipo: 'fechado' }
  | { tipo: 'novo' }
  | { tipo: 'editar'; servico: ServicoResumo };

export function ServicosPage() {
  const { servicos, carregando, erro, recarregar, atualizar, excluir } = useServicos();
  // Criar e editar têm endereço próprio (`/servicos/novo`,
  // `/servicos/:id/editar`).
  const rota = useRotaEntidade(ROTAS.servicos);
  const [busca, setBusca] = useState('');
  const dialogos = useDialogos();

  const modo = useMemo<ModoForm>(() => {
    if (rota.modo === 'novo') return { tipo: 'novo' };
    if (rota.modo === 'editar') {
      const registro = servicos.find((x) => x.id === rota.id);
      return registro ? { tipo: 'editar', servico: registro } : { tipo: 'fechado' };
    }
    return { tipo: 'fechado' };
  }, [rota, servicos]);

  const filtrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    if (q === '') return servicos;
    return servicos.filter((s) =>
      normalizarBusca(`${s.codigo} ${s.nome}`).includes(q),
    );
  }, [servicos, busca]);

  const { pagina, setPagina, paginados, total, totalPaginas, tamanhoPagina } =
    usePaginacao(filtrados, 25);

  async function confirmarExclusao(servico: ServicoResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Excluir serviço',
      mensagem: `Excluir o serviço "${servico.nome}"?`,
      textoConfirmar: 'Excluir',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await excluir(servico.id);
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao excluir.');
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Serviços"
        descricao="Atividades cobradas para compor ordens de serviço."
        acoes={
          <>
            <Button onClick={() => void recarregar()} disabled={carregando}>
              <IconRefresh className="btn__icon" />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </Button>
            <Button variante="primary" onClick={() => rota.abrir('novo')}>
              + Novo serviço
            </Button>
          </>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          Falha ao carregar: {erro}
        </p>
      )}

      <div className="card">
        <div className="card__header card__header--com-busca">
          <span className="card__title">
            {filtrados.length} de {servicos.length} serviço(s)
          </span>
          <SearchInput
            valor={busca}
            onChange={setBusca}
            placeholder="Buscar por código ou nome…"
            ariaLabel="Buscar serviço"
          />
        </div>
        <div className="card__body card__body--flush">
          {servicos.length === 0 && !carregando && !erro ? (
            <EmptyState
              icone={<IconWrench />}
              titulo="Nenhum serviço cadastrado ainda."
              descricao="Cadastre serviços para utilizá-los nas ordens de serviço."
            />
          ) : filtrados.length === 0 ? (
            <EmptyState
              icone={<IconWrench />}
              titulo="Nenhum serviço corresponde à busca."
              descricao="Tente outro termo ou limpe o campo de busca."
            />
          ) : (
            <div className="table-scroll"><table className="table table--responsive">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th className="table__numeric">Preço</th>
                  <th>Status</th>
                  <th style={{ width: 1 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((s) => (
                  <tr key={s.id}>
                    <td className="table__primary">{s.codigo}</td>
                    <td data-label="Nome">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {s.nome}
                        <BadgeSyncPendente pendente={s.syncPendente} />
                      </span>
                    </td>
                    <td className="table__numeric" data-label="Preço">
                      {formatadorMoeda.format(s.precoUnitario)}
                    </td>
                    <td data-label="Status">
                      {s.ativo ? (
                        <span className="badge badge--ativo">Ativo</span>
                      ) : (
                        <span className="badge badge--inativo">(inativo)</span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <Button
                          className="btn--icon"
                          aria-label={`Editar ${s.nome}`}
                          onClick={() => rota.abrir(`${s.id}/editar`)}
                        >
                          <IconEdit />
                        </Button>
                        <Button
                          className="btn--icon btn--danger"
                          aria-label={`Excluir ${s.nome}`}
                          onClick={() => void confirmarExclusao(s)}
                        >
                          <IconTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          <Paginacao
            pagina={pagina}
            totalPaginas={totalPaginas}
            total={total}
            tamanhoPagina={tamanhoPagina}
            onMudarPagina={setPagina}
          />
        </div>
      </div>

      {modo.tipo !== 'fechado' && (
        <ServicoFormModal
          modo={modo}
          aoFechar={rota.fechar}
          aoSalvar={async (dados) => {
            if (modo.tipo === 'editar') {
              await atualizar(modo.servico.id, dados);
            }
            rota.fechar();
            await recarregar();
          }}
        />
      )}
    </section>
  );
}
